/**
 * Dashboard / Postharvest.
 *
 * Dos widgets con pantalla completa independiente:
 *
 *  1. "Próximos despachos" — grid de 3 días (33% cada uno) con los pedidos
 *     programados. Cada tarjeta de pedido se puede abrir en un modal con
 *     todo el detalle (items, cajas, totales, cliente final, OCs ligadas).
 *     Nav ← / → mueve el ancla ±3 días; "Hoy" resetea.
 *
 *  2. "Inventario actual" — por variedad, listando cada stemLength con el
 *     desglose por etapa (sin procesar / en proceso / cuarto frío). El
 *     usuario pidió poder ver "cuántos Ruscus hay de 60cm vs 30cm".
 *
 * El botón de pantalla completa de cada widget llama a requestFullscreen()
 * sobre el wrapper correspondiente. `useFullscreen` escucha
 * `fullscreenchange` para re-sincronizar si el usuario sale con Esc.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Leaf,
  Maximize2,
  Minimize2,
  Package,
  Snowflake,
  Scissors,
  ShoppingCart,
  Sparkles,
  TrendingUp,
} from 'lucide-react'
import {
  SALE_STATUS_LABELS,
  SALE_STATUS_LEGACY_MAP,
} from '@expogreens/shared'
import { Card, CardContent } from '@/components/ui/Card'
import { ModuleShell } from '@/components/ui/ModuleShell'
import { Button } from '@/components/ui/Button'
import { Segmented } from '@/components/ui/Segmented'
import { FilterBar, DateMenu } from '@/components/ui/FilterBar'
import { Modal } from '@/components/ui/Modal'
import { useApi } from '@/lib/useApi'
import { apiCall } from '@/lib/api'
import { cn } from '@/lib/utils'

const STAGE_LABEL = {
  unprocessed: 'Sin procesar',
  // Por feedback del cliente, "En proceso" se oculta de la UI del dashboard.
  in_process: 'En proceso',
  cold_room: 'Procesado',
}

const STAGE_ICON = {
  unprocessed: Sparkles,
  in_process: Leaf,
  cold_room: Snowflake,
}

const STAGE_COLOR = {
  unprocessed: 'bg-amber-100 text-amber-800',
  in_process: 'bg-blue-100 text-blue-800',
  cold_room: 'bg-sky-100 text-sky-900',
}

// Periodos del filtro del Dashboard. 'custom' permite from/to.
const PERIOD_OPTIONS = [
  { id: 'today', label: 'Hoy' },
  { id: 'week', label: 'Semana (Lun–Dom)' },
  { id: 'month', label: 'Mes' },
  { id: 'custom', label: 'Personalizado' },
]

// Color de chip por status del pedido — usado en la tarjeta de venta.
const SALE_STATUS_COLOR = {
  confirmed: 'bg-gray-100 text-gray-700',
  supplying: 'bg-amber-100 text-amber-800',
  processing: 'bg-blue-100 text-blue-800',
  dispatched: 'bg-emerald-100 text-emerald-800',
  delivered: 'bg-emerald-200 text-emerald-900',
  cancelled: 'bg-red-100 text-red-800',
}

// Badge por status estilo handoff #42 (fondo suave + texto fuerte).
const SALE_STATUS_BADGE = {
  confirmed: { bg: '#E3F0D5', fg: '#1E6E3A' },
  supplying: { bg: '#FFF6E0', fg: '#B07D17' },
  processing: { bg: '#FFF6E0', fg: '#B07D17' },
  dispatched: { bg: '#DCEAD9', fg: '#14502A' },
  delivered: { bg: '#DCEAD9', fg: '#14502A' },
  cancelled: { bg: '#FDECEC', fg: '#C0392B' },
}

/** Estilo inline del badge de status para el modal de detalle. */
function statusBadgeStyle(status) {
  const c = SALE_STATUS_BADGE[status] || { bg: '#F0F3EE', fg: '#5C6B60' }
  return {
    display: 'inline-flex',
    alignItems: 'center',
    background: c.bg,
    color: c.fg,
    fontSize: 12,
    fontWeight: 700,
    padding: '5px 12px',
    borderRadius: 999,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  }
}

/** Status efectivo del pedido (mapeando los valores legacy del seed v1). */
function effectiveStatus(sale) {
  const raw = sale?.status || 'confirmed'
  return SALE_STATUS_LEGACY_MAP[raw] || raw
}

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
const MONTH_NAMES_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

// Nº de días visibles en el widget de despachos. Lo mantenemos en 3 para
// que cada tarjeta tenga ~33% del ancho; si el usuario quiere más, avanza
// con las flechas.
const STRIP_DAYS = 3

function startOfDay(d) {
  const copy = new Date(d)
  copy.setHours(0, 0, 0, 0)
  return copy
}

function addDays(d, n) {
  const copy = new Date(d)
  copy.setDate(copy.getDate() + n)
  return copy
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function ymd(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Hook que controla el Fullscreen API sobre un elemento. */
function useFullscreen() {
  const ref = useRef(null)
  const [isFs, setIsFs] = useState(false)
  useEffect(() => {
    const onChange = () =>
      setIsFs(!!document.fullscreenElement && document.fullscreenElement === ref.current)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])
  const toggle = async () => {
    try {
      if (document.fullscreenElement === ref.current) {
        await document.exitFullscreen?.()
        return
      }
      if (document.fullscreenElement) {
        await document.exitFullscreen?.()
      }
      await ref.current?.requestFullscreen?.()
    } catch {
      /* silencioso — algunos navegadores bloquean sin gesto */
    }
  }
  return { ref, isFs, toggle }
}

/**
 * Tarjeta de KPI estilo handoff #27: label en caps, valor grande y una
 * "píldora" de contexto debajo (▲ + dato). `hero` la pinta verde sólida con
 * píldora lima; el resto son blancas con icono y píldora de tinte verde.
 * Un círculo decorativo difuminado en la esquina le da textura.
 */
function Kpi({ icon: Icon, label, value, pill, hero = false }) {
  return (
    <div
      className="hover-lift"
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        background: hero ? '#033021' : '#fff',
        border: hero ? '1px solid #1E6E3A' : '1px solid #E6EFE3',
        borderRadius: 18,
        padding: 22,
        color: hero ? '#fff' : undefined,
        boxShadow: hero ? '0 10px 24px rgba(3,48,33,0.28)' : 'none',
      }}
    >
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: hero ? undefined : '#8B9B8F',
            opacity: hero ? 0.85 : 1,
          }}
        >
          {label}
        </div>
        {Icon ? (
          <div
            className="flex-none flex items-center justify-center"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: hero ? 'rgba(195,219,90,0.22)' : '#E3F0D5',
              color: hero ? '#C3DB5A' : '#1E6E3A',
            }}
          >
            <Icon size={20} />
          </div>
        ) : null}
      </div>
      <div
        className="font-display"
        style={{
          position: 'relative',
          fontSize: hero ? 32 : 34,
          fontWeight: 800,
          lineHeight: 1.05,
          color: hero ? '#fff' : '#1E6E3A',
          margin: '10px 0 12px',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </div>
      {pill ? (
        <div
          className="inline-flex items-center"
          style={{
            position: 'relative',
            alignSelf: 'flex-start',
            gap: 5,
            background: hero ? 'rgba(195,219,90,0.25)' : '#E3F0D5',
            color: hero ? '#D8EE8E' : '#1E6E3A',
            fontSize: 13,
            fontWeight: 700,
            padding: '5px 11px',
            borderRadius: 999,
            marginTop: 'auto',
          }}
        >
          <TrendingUp size={13} strokeWidth={2.6} />
          {pill}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Resume una venta en "líneas de caja" y "sueltos".
 * Usa sale.boxes[] si hay, si no cae a sale.items[].
 */
function summarizeSale(sale) {
  const boxLines = []
  for (const b of sale.boxes || []) {
    const count = Number(b.boxCount) || 0
    if (count <= 0) continue
    const items = (b.items || [])
      .filter((it) => (Number(it.quantity) || 0) > 0)
      .map((it) => ({
        variety: it.variety,
        quantity: Number(it.quantity) || 0,
        stemLength: it.stemLength,
      }))
    if (items.length === 0) continue
    boxLines.push({
      boxCount: count,
      boxSize: b.boxSize,
      stemsPerBox: Number(b.stemsPerBox) || 0,
      items,
    })
  }
  const looseLines =
    boxLines.length === 0
      ? (sale.items || [])
          .filter((it) => (Number(it.quantity) || 0) > 0)
          .map((it) => ({
            variety: it.variety,
            quantity: Number(it.quantity) || 0,
            stemLength: it.stemLength,
            priceUSD: Number(it.priceUSD) || 0,
          }))
      : []
  return { boxLines, looseLines }
}

/** "20 Cajas, cada caja con 30 Ruscus de 50cm, 10 Ruscus de 20cm" */
function BoxLine({ line, compact = false }) {
  const detail = line.items
    .map((it) => `${it.quantity} ${it.variety} de ${it.stemLength}cm`)
    .join(', ')
  return (
    <div className={cn('text-expo-dark', compact ? 'text-sm' : 'text-base')}>
      <span className="font-semibold">{line.boxCount} Cajas</span>
      <span className="text-gray-600">, cada caja con {detail}</span>
    </div>
  )
}

function LooseLine({ line, compact = false }) {
  return (
    <div className={cn('text-expo-dark', compact ? 'text-sm' : 'text-base')}>
      <span className="font-semibold">{line.quantity}</span>
      <span className="text-gray-600">
        {' '}
        {line.variety} de {line.stemLength}cm
      </span>
    </div>
  )
}

/**
 * Resuelve la etiqueta de cliente a mostrar, priorizando siempre al cliente
 * FINAL (al que se envía realmente) y entre paréntesis al mayorista si
 * existe. Dos fuentes posibles de jerarquía, en este orden:
 *
 *   1. El modelo nuevo: `sale.clientId` apunta a un cliente que a su vez
 *      tiene `parentClientId`. En ese caso, el final = ese mismo cliente,
 *      el mayorista = clientsById[parentClientId].
 *   2. El modelo legacy (seed): `sale.finalClientName` embebido. En ese caso
 *      final = finalClientName y el mayorista = sale.clientName.
 */
function resolveClientLabels(sale, clientsById) {
  const primary = clientsById?.get?.(sale.clientId)
  const parent = primary?.parentClientId
    ? clientsById?.get?.(primary.parentClientId)
    : null

  if (parent) {
    return {
      final: primary?.name || sale.clientName || sale.clientId,
      wholesale: parent.name,
    }
  }
  if (sale.finalClientName) {
    return {
      final: sale.finalClientName,
      wholesale: sale.clientName || sale.clientId,
    }
  }
  return {
    final: primary?.name || sale.clientName || sale.clientId,
    wholesale: null,
  }
}

/**
 * Aplana items+cajas a líneas compactas "1000 Ruscus 60cm". Las cajas se
 * multiplican por boxCount para reflejar tallos totales, no por caja.
 */
function flattenSaleLines(sale) {
  const lines = []
  for (const b of sale.boxes || []) {
    const count = Number(b.boxCount) || 0
    if (count <= 0) continue
    for (const it of b.items || []) {
      const q = (Number(it.quantity) || 0) * count
      if (q <= 0) continue
      lines.push({ variety: it.variety, quantity: q, stemLength: it.stemLength })
    }
  }
  if (lines.length === 0) {
    for (const it of sale.items || []) {
      const q = Number(it.quantity) || 0
      if (q <= 0) continue
      lines.push({ variety: it.variety, quantity: q, stemLength: it.stemLength })
    }
  }
  return lines
}

function SaleCard({ sale, onOpen, clientsById }) {
  const labels = resolveClientLabels(sale, clientsById)
  const lines = flattenSaleLines(sale)
  const totalStems =
    Number(sale.totalStems) || lines.reduce((a, l) => a + l.quantity, 0)
  const effStatus = effectiveStatus(sale)

  const badge = SALE_STATUS_BADGE[effStatus] || { bg: '#E3F0D5', fg: '#1E6E3A' }

  return (
    <button
      type="button"
      onClick={() => onOpen?.(sale)}
      className="text-left w-full active:scale-[0.99] transition-all cursor-pointer"
      style={{
        background: '#fff',
        border: '1px solid #EAEFE7',
        borderRadius: 14,
        padding: 16,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#1E6E3A')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#EAEFE7')}
      title="Click para ver el detalle completo"
    >
      <div
        className="flex items-center justify-between gap-2"
        style={{ marginBottom: 10 }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: '#1E6E3A',
            whiteSpace: 'nowrap',
          }}
        >
          {sale.id}
        </span>
        {sale.dispatchTime ? (
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#9BA89B',
              whiteSpace: 'nowrap',
              flex: 'none',
            }}
          >
            {sale.dispatchTime}
          </span>
        ) : null}
      </div>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          background: badge.bg,
          color: badge.fg,
          fontSize: 12,
          fontWeight: 700,
          padding: '5px 11px',
          borderRadius: 999,
          whiteSpace: 'nowrap',
        }}
      >
        {SALE_STATUS_LABELS[effStatus] || effStatus}
      </span>
      <div
        className="font-display truncate"
        style={{
          fontSize: 17,
          fontWeight: 800,
          color: '#143A22',
          lineHeight: 1.2,
          margin: '12px 0 2px',
        }}
        title={labels.final}
      >
        {labels.final}
      </div>
      {labels.wholesale && (
        <div
          className="truncate"
          style={{
            fontSize: 12,
            color: '#8B9B8F',
            fontWeight: 500,
            marginBottom: 10,
          }}
          title={labels.wholesale}
        >
          Mayorista · {labels.wholesale}
        </div>
      )}
      {lines.length > 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            marginTop: 8,
          }}
        >
          {lines.map((l, i) => (
            <div
              key={i}
              className="truncate"
              style={{ fontSize: 13, color: '#3F4F44' }}
            >
              <b style={{ fontWeight: 800, color: '#143A22' }}>
                {l.quantity.toLocaleString('es-CO')}
              </b>{' '}
              {l.variety}
              {l.stemLength ? ` · ${l.stemLength}cm` : ''}
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{ marginTop: 8, fontSize: 13, fontStyle: 'italic', color: '#8B9B8F' }}
        >
          Sin items
        </div>
      )}
      <div
        className="flex items-center justify-between gap-2"
        style={{
          marginTop: 13,
          paddingTop: 12,
          borderTop: '1px solid #F0F3ED',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: '#9BA89B' }}>
          {totalStems.toLocaleString('es-CO')} tallos
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: '#1E6E3A',
            whiteSpace: 'nowrap',
          }}
        >
          ver detalle →
        </span>
      </div>
    </button>
  )
}

function DayCard({
  date,
  sales,
  isToday,
  isWeekend,
  onOpenSale,
  fullscreen,
  clientsById,
}) {
  const totalStems = sales.reduce((acc, s) => acc + (Number(s.totalStems) || 0), 0)
  return (
    <div
      className="flex flex-col h-full"
      style={{
        border: '1px solid #EEF2EC',
        borderRadius: 16,
        overflow: 'hidden',
        background: '#FCFEFA',
      }}
    >
      <div
        style={
          isToday
            ? { background: '#033021', padding: '16px 18px 14px' }
            : { padding: '16px 18px 14px', borderBottom: '1px solid #EEF2EC' }
        }
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: isToday ? 'rgba(255,255,255,0.7)' : '#A9B4AA',
          }}
        >
          {DAY_NAMES[date.getDay()]}
        </div>
        <div
          className="font-display"
          style={{
            fontSize: fullscreen ? 28 : 24,
            fontWeight: 800,
            color: isToday ? '#fff' : '#143A22',
            lineHeight: 1.1,
            margin: '4px 0 5px',
          }}
        >
          {date.getDate()} {MONTH_NAMES_SHORT[date.getMonth()]}
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: isToday ? 'rgba(255,255,255,0.8)' : '#8B9B8F',
          }}
        >
          {sales.length} pedido{sales.length === 1 ? '' : 's'} ·{' '}
          {totalStems.toLocaleString('es-CO')} tallos
        </div>
      </div>
      <div
        className={cn('overflow-y-auto', fullscreen && 'flex-1')}
        style={{
          padding: 16,
          flex: 1,
          minHeight: 190,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {sales.length === 0 && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              color: '#B3BEB4',
              fontSize: 13,
              fontStyle: 'italic',
            }}
          >
            Sin pedidos programados
          </div>
        )}
        {sales.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sales.map((sale) => (
              <SaleCard
                key={sale.id}
                sale={sale}
                onOpen={onOpenSale}
                clientsById={clientsById}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Acciones manuales de status disponibles desde el detalle del pedido.
 * Solo se exponen las transiciones que el usuario puede disparar a mano:
 * Despachado, Entregado, Cancelar.
 *
 * - "Despachado" pega contra POST /sales/:id/status. Si el backend
 *   responde inventory_not_processed, se muestra "Gestionar inventario"
 *   con el detalle de lo que falta.
 * - "Entregado" no requiere validación extra.
 * - "Cancelar" pide motivo opcional.
 */
function SaleStatusActions({ sale, onStatusChanged }) {
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)

  const eff = effectiveStatus(sale)
  const canDispatch = ['confirmed', 'supplying', 'processing'].includes(eff)
  const canDeliver = eff === 'dispatched'
  const canCancel = !['delivered', 'cancelled', 'dispatched'].includes(eff)

  async function transition(targetStatus, note) {
    setBusy(targetStatus)
    setError(null)
    try {
      await apiCall(`/sales/${sale.id}/status`, {
        method: 'POST',
        body: { status: targetStatus, note: note || null },
      })
      onStatusChanged?.()
    } catch (err) {
      // El apiCall lanza con err.message = JSON serializado. Intento parsear
      // para mostrar el mensaje del backend (ej. inventory_not_processed).
      let msg = err?.message || 'Error al cambiar el estado'
      try {
        const parsed = JSON.parse(msg)
        if (parsed?.error === 'inventory_not_processed') {
          const miss = (parsed.missing || [])
            .map(
              (m) =>
                `${m.variety}${m.stemLength ? ` ${m.stemLength}cm` : ''}: faltan ${(m.needed - m.available).toLocaleString('es-CO')} (hay ${m.available}, requiere ${m.needed})`,
            )
            .join(' · ')
          msg = `Gestionar inventario — ${miss}`
        } else if (parsed?.error) {
          msg = parsed.error
        }
      } catch {
        /* no era JSON */
      }
      setError(String(msg))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="rounded-xl border border-expo-line bg-expo-light/30 p-3">
      <div className="text-sm font-semibold text-expo-dark mb-2">
        Cambiar estado del pedido
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant={canDispatch ? 'primary' : 'secondary'}
          onClick={() => transition('dispatched')}
          disabled={!canDispatch || !!busy}
        >
          {busy === 'dispatched' ? 'Despachando…' : 'Marcar como Despachado'}
        </Button>
        <Button
          type="button"
          variant={canDeliver ? 'primary' : 'secondary'}
          onClick={() => transition('delivered')}
          disabled={!canDeliver || !!busy}
        >
          {busy === 'delivered' ? 'Marcando…' : 'Marcar como Entregado'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            const reason = window.prompt('Motivo de la cancelación (opcional):', '')
            // Permite cancelar sin motivo: solo cortar si el usuario hace Esc.
            if (reason === null) return
            transition('cancelled', reason)
          }}
          disabled={!canCancel || !!busy}
        >
          {busy === 'cancelled' ? 'Cancelando…' : 'Cancelar pedido'}
        </Button>
      </div>
      {error && (
        <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
      <p className="mt-2 text-[11px] text-gray-500">
        Confirmado, En abastecimiento y En procesamiento se gestionan
        automáticamente (al crear venta, al crear OC ligada y al recibir
        producto). Despachado / Entregado son manuales.
      </p>
    </div>
  )
}

function SaleDetailModal({ sale, onClose, clientsById, onStatusChanged }) {
  if (!sale) return null
  const labels = resolveClientLabels(sale, clientsById)
  const { boxLines, looseLines } = summarizeSale(sale)
  const effStatus = effectiveStatus(sale)
  const totalStems =
    Number(sale.totalStems) ||
    boxLines.reduce(
      (a, b) => a + b.items.reduce((x, it) => x + it.quantity, 0) * (b.boxCount || 1),
      0,
    ) +
      looseLines.reduce((a, it) => a + it.quantity, 0)
  return (
    <Modal
      open={Boolean(sale)}
      onClose={onClose}
      title={`Pedido ${sale.id}`}
      subtitle={labels.final}
      size="xl"
    >
      <div className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div
              className="text-[11px] uppercase font-bold"
              style={{ letterSpacing: '0.04em', color: '#8B9B8F' }}
            >
              {labels.wholesale ? 'Cliente final' : 'Cliente'}
            </div>
            <div
              className="font-display mt-1"
              style={{ fontSize: 19, fontWeight: 800, color: '#143A22' }}
            >
              {labels.final}
            </div>
            {labels.wholesale && (
              <div className="text-[11px] mt-0.5" style={{ color: '#8B9B8F' }}>
                mayorista: {labels.wholesale}
              </div>
            )}
          </div>
          <div>
            <div
              className="text-[11px] uppercase font-bold"
              style={{ letterSpacing: '0.04em', color: '#8B9B8F' }}
            >
              Entrega
            </div>
            <div
              className="font-display mt-1"
              style={{ fontSize: 19, fontWeight: 800, color: '#143A22' }}
            >
              {sale.deliveryDate || '—'}
            </div>
            {sale.dispatchTime && (
              <div className="text-[11px] mt-0.5" style={{ color: '#8B9B8F' }}>
                {sale.dispatchTime}
              </div>
            )}
          </div>
          <div>
            <div
              className="text-[11px] uppercase font-bold"
              style={{ letterSpacing: '0.04em', color: '#8B9B8F' }}
            >
              Estado
            </div>
            <div className="mt-1.5">
              <span style={statusBadgeStyle(effStatus)}>
                {SALE_STATUS_LABELS[effStatus] || effStatus}
              </span>
            </div>
            {sale.source && (
              <div className="text-[11px] mt-1" style={{ color: '#8B9B8F' }}>
                origen: {sale.source}
              </div>
            )}
          </div>
          <div>
            <div
              className="text-[11px] uppercase font-bold"
              style={{ letterSpacing: '0.04em', color: '#8B9B8F' }}
            >
              Totales
            </div>
            <div
              className="font-display mt-1"
              style={{ fontSize: 19, fontWeight: 800, color: '#1E6E3A' }}
            >
              {totalStems.toLocaleString('es-CO')}{' '}
              <span style={{ fontSize: 13, color: '#8B9B8F', fontWeight: 600 }}>
                tallos
              </span>
            </div>
          </div>
        </div>

        <SaleStatusActions sale={sale} onStatusChanged={onStatusChanged} />

        {boxLines.length > 0 && (
          <div>
            <div
              className="font-display mb-2"
              style={{ fontSize: 18, fontWeight: 800, color: '#143A22' }}
            >
              Cajas
            </div>
            <div
              style={{
                border: '1px solid #E6EFE3',
                borderRadius: 14,
                overflow: 'hidden',
              }}
            >
              {boxLines.map((line, i) => (
                <div
                  key={i}
                  className="p-3"
                  style={i > 0 ? { borderTop: '1px solid #F2F5F0' } : undefined}
                >
                  <div
                    style={{ fontSize: 15, fontWeight: 700, color: '#143A22' }}
                  >
                    {line.boxCount} Cajas {line.boxSize && `(${line.boxSize})`}
                    {line.stemsPerBox
                      ? ` · ${line.stemsPerBox} tallos/caja`
                      : ''}
                  </div>
                  <ul className="mt-1 text-sm list-disc list-inside" style={{ color: '#5C6B60' }}>
                    {line.items.map((it, j) => (
                      <li key={j}>
                        <span className="font-semibold" style={{ color: '#143A22' }}>
                          {it.quantity}
                        </span>{' '}
                        {it.variety} de {it.stemLength}cm
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {looseLines.length > 0 && (
          <div>
            <div
              className="font-display mb-2"
              style={{ fontSize: 18, fontWeight: 800, color: '#143A22' }}
            >
              Tallos sueltos
            </div>
            <div
              style={{
                border: '1px solid #E6EFE3',
                borderRadius: 14,
                overflow: 'hidden',
              }}
            >
              {/*
               * Por petición del cliente se quitan las columnas con valor
               * monetario (precio USD) del dashboard — es información
               * interna y no debe mostrarse aquí.
               */}
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#F6FAEF' }}>
                    <th
                      className="text-left px-3 py-2 uppercase"
                      style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: '#9BA89B' }}
                    >
                      Variedad
                    </th>
                    <th
                      className="text-right px-3 py-2 uppercase"
                      style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: '#9BA89B' }}
                    >
                      Cantidad
                    </th>
                    <th
                      className="text-right px-3 py-2 uppercase"
                      style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: '#9BA89B' }}
                    >
                      Longitud
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {looseLines.map((it, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #F2F5F0' }}>
                      <td className="px-3 py-2" style={{ color: '#143A22', fontWeight: 600 }}>
                        {it.variety}
                      </td>
                      <td className="px-3 py-2 text-right" style={{ color: '#143A22' }}>
                        {it.quantity.toLocaleString('es-CO')}
                      </td>
                      <td className="px-3 py-2 text-right" style={{ color: '#5C6B60' }}>
                        {it.stemLength}cm
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {sale.linkedPurchaseOrders && sale.linkedPurchaseOrders.length > 0 && (
          <div>
            <div
              className="font-display mb-2"
              style={{ fontSize: 18, fontWeight: 800, color: '#143A22' }}
            >
              Órdenes de compra ligadas
            </div>
            <div className="flex flex-wrap gap-2">
              {sale.linkedPurchaseOrders.map((id) => (
                <span
                  key={id}
                  className="text-xs font-mono px-2.5 py-1"
                  style={{
                    background: '#F6FAEF',
                    border: '1px solid #E6EFE3',
                    borderRadius: 8,
                    color: '#1E6E3A',
                    fontWeight: 600,
                  }}
                >
                  {id}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="text-[11px]" style={{ color: '#9BA89B' }}>
          Creada {sale.createdAt ? new Date(sale.createdAt).toLocaleString('es-CO') : '—'}
        </div>
      </div>
    </Modal>
  )
}

/**
 * Resuelve el rango de fechas a mostrar a partir del periodo del Dashboard.
 *
 *   today  → solo hoy
 *   week   → Lun-Dom de la semana actual
 *   month  → mes calendario actual
 *   custom → range.from / range.to (inclusive)
 *
 * Devuelve { startDate, endDate, dayCount, anchored } donde `anchored`
 * indica si el rango está fijado por el filtro o si el usuario puede
 * navegar con flechas (caso fallback).
 */
function resolveDispatchRange(period, range) {
  const now = startOfDay(new Date())
  if (period === 'today') {
    return { startDate: now, endDate: now, dayCount: 1, anchored: true }
  }
  if (period === 'week') {
    const day = now.getDay() // 0 dom .. 6 sab
    const offsetToMonday = day === 0 ? -6 : 1 - day
    const monday = addDays(now, offsetToMonday)
    return {
      startDate: monday,
      endDate: addDays(monday, 6),
      dayCount: 7,
      anchored: true,
    }
  }
  if (period === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return {
      startDate: startOfDay(start),
      endDate: startOfDay(end),
      dayCount:
        Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1,
      anchored: true,
    }
  }
  if (period === 'custom' && range?.from && range?.to) {
    const start = startOfDay(new Date(range.from))
    const end = startOfDay(new Date(range.to))
    if (
      !Number.isNaN(start.getTime()) &&
      !Number.isNaN(end.getTime()) &&
      end >= start
    ) {
      return {
        startDate: start,
        endDate: end,
        dayCount:
          Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1,
        anchored: true,
      }
    }
  }
  // Sin filtro válido → comportamiento clásico de 3 días desde hoy con
  // navegación por flechas.
  return { startDate: null, endDate: null, dayCount: 0, anchored: false }
}

/**
 * Props:
 *   sales, onOpenSale, clientsById, period, range
 *
 * `period` y `range` vienen del filtro del Dashboard. Cuando hay un
 * rango anclado por el filtro, el widget muestra ESE rango (sin flechas
 * de navegación). Si no, mantiene el modo clásico de strip navegable.
 *
 * onStatusChanged se acepta pero ya no se usa aquí; el cambio de
 * estado se hace desde el modal de detalle.
 */
function UpcomingDispatches({
  sales,
  onOpenSale,
  clientsById,
  period,
  range,
}) {
  const fs = useFullscreen()
  // Anchor solo se usa cuando el periodo NO fija un rango (fallback).
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()))

  const salesByDate = useMemo(() => {
    const map = new Map()
    for (const s of sales || []) {
      // Match the Sales page's fallback chain so we don't silently drop
      // sales that have saleDate/date but no explicit deliveryDate.
      const key = String(s.deliveryDate || s.saleDate || s.date || '').slice(0, 10)
      if (!key) continue
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(s)
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.dispatchTime || '').localeCompare(b.dispatchTime || ''))
    }
    return map
  }, [sales])

  // Ventana de 3 días desde el ancla (diseño #42 "Agenda de despachos").
  // La navegación prev/next mueve el ancla ±3 días; "Hoy" la resetea.
  const days = useMemo(() => {
    const out = []
    for (let i = 0; i < STRIP_DAYS; i++) out.push(addDays(anchor, i))
    return out
  }, [anchor])

  const today = startOfDay(new Date())

  const rangeLabel = useMemo(() => {
    if (days.length === 0) return ''
    const first = days[0]
    const last = days[days.length - 1]
    if (sameDay(first, last)) {
      return `${first.getDate()} ${MONTH_NAMES_SHORT[first.getMonth()]} ${first.getFullYear()}`
    }
    return `${first.getDate()} ${MONTH_NAMES_SHORT[first.getMonth()]} – ${last.getDate()} ${MONTH_NAMES_SHORT[last.getMonth()]} ${last.getFullYear()}`
  }, [days])

  const roundBtnStyle = {
    width: 44,
    height: 44,
    borderRadius: 999,
    border: '1.5px solid #D7E3D2',
    background: '#fff',
    color: '#1E6E3A',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
  }

  return (
    <div
      ref={fs.ref}
      className={cn('mb-8', fs.isFs && 'p-6 h-screen flex flex-col')}
      style={
        fs.isFs
          ? { background: '#fff' }
          : {
              background: '#fff',
              border: '1px solid #E6EFE3',
              borderRadius: 22,
              padding: '28px 30px',
            }
      }
    >
      <div className={cn(fs.isFs && 'pb-4 flex-shrink-0')}>
        <div
          className="flex flex-wrap"
          style={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 20,
            marginBottom: 22,
          }}
        >
          <div>
            <h3
              className="font-display"
              style={{
                fontSize: fs.isFs ? 32 : 26,
                fontWeight: 800,
                color: '#143A22',
                lineHeight: 1,
              }}
            >
              Próximos despachos
            </h3>
            <div
              style={{
                fontSize: 15,
                color: '#8B9B8F',
                fontWeight: 500,
                marginTop: 6,
              }}
            >
              {rangeLabel}
            </div>
          </div>
          <div className="flex items-center" style={{ gap: 10, flex: 'none' }}>
            <button
              type="button"
              style={roundBtnStyle}
              onClick={() => setAnchor((a) => addDays(a, -STRIP_DAYS))}
              title="Días anteriores"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => setAnchor(startOfDay(new Date()))}
              title="Hoy"
              style={{
                height: 44,
                borderRadius: 999,
                border: '1.5px solid #D7E3D2',
                background: '#fff',
                color: '#1E6E3A',
                cursor: 'pointer',
                padding: '0 16px',
                fontSize: 14,
                fontWeight: 700,
                flex: 'none',
              }}
            >
              Hoy
            </button>
            <button
              type="button"
              style={roundBtnStyle}
              onClick={() => setAnchor((a) => addDays(a, STRIP_DAYS))}
              title="Días siguientes"
            >
              <ChevronRight size={18} />
            </button>
            <button
              type="button"
              style={roundBtnStyle}
              onClick={fs.toggle}
              title={fs.isFs ? 'Salir de pantalla completa' : 'Pantalla completa'}
            >
              {fs.isFs ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
            </button>
          </div>
        </div>

        {/* Grilla de 3 columnas (diseño #42). Cada columna es un día. */}
        <div
          className={cn(
            'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
            fs.isFs && 'flex-1 min-h-0',
          )}
        >
          {days.map((d) => (
            <DayCard
              key={ymd(d)}
              date={d}
              sales={salesByDate.get(ymd(d)) || []}
              isToday={sameDay(d, today)}
              isWeekend={d.getDay() === 0 || d.getDay() === 6}
              onOpenSale={onOpenSale}
              fullscreen={fs.isFs}
              clientsById={clientsById}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Agrupa el inventario por variedad con un sub-listado por stemLength.
 *   [{ variety, lengths:[{stemLength, stages:{unprocessed,in_process,cold_room}, total}], total }]
 */
function groupInventoryByVariety(rows) {
  const map = new Map()
  for (const r of rows || []) {
    const key = r.variety || '—'
    if (!map.has(key)) map.set(key, [])
    map.get(key).push({
      id: r.id,
      stemLength: Number(r.stemLength) || 0,
      stages: {
        unprocessed: Number(r.stages?.unprocessed?.total) || 0,
        in_process: Number(r.stages?.in_process?.total) || 0,
        cold_room: Number(r.stages?.cold_room?.total) || 0,
      },
    })
  }
  const out = []
  for (const [variety, arr] of map) {
    arr.sort((a, b) => a.stemLength - b.stemLength)
    const withTotals = arr.map((r) => ({
      ...r,
      total: r.stages.unprocessed + r.stages.in_process + r.stages.cold_room,
    }))
    const total = withTotals.reduce((a, r) => a + r.total, 0)
    out.push({ variety, lengths: withTotals, total })
  }
  out.sort((a, b) => b.total - a.total || a.variety.localeCompare(b.variety, 'es'))
  return out
}

function InventoryDetail({ inventory }) {
  const fs = useFullscreen()
  const groups = useMemo(() => groupInventoryByVariety(inventory), [inventory])

  const stageTotals = useMemo(() => {
    const t = { unprocessed: 0, in_process: 0, cold_room: 0 }
    for (const g of groups) {
      for (const r of g.lengths) {
        t.unprocessed += r.stages.unprocessed
        t.in_process += r.stages.in_process
        t.cold_room += r.stages.cold_room
      }
    }
    return t
  }, [groups])

  return (
    <div
      ref={fs.ref}
      className={cn(
        'rounded-[20px] border border-expo-line bg-white shadow-sm',
        fs.isFs && 'p-6 h-screen overflow-auto',
      )}
    >
      <div className="p-6">
        {/* Cabecera estilo #40: título + subtítulo a la izquierda,
            total grande + "TALLOS · TOTAL" a la derecha. */}
        <div className="flex items-start justify-between gap-6 flex-wrap mb-5">
          <div>
            <div
              className={cn('font-display font-extrabold leading-tight', fs.isFs ? 'text-[34px]' : 'text-[26px]')}
              style={{ color: '#143A22', letterSpacing: '-0.01em' }}
            >
              Inventario actual por variedad y longitud
            </div>
            <div className="mt-1 text-[15px] font-medium" style={{ color: '#8B9B8F' }}>
              Estado de procesamiento del stock disponible
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="text-right">
              <div
                className={cn('font-display font-extrabold leading-none', fs.isFs ? 'text-[44px]' : 'text-[30px]')}
                style={{ color: '#1E6E3A' }}
              >
                {(stageTotals.unprocessed + stageTotals.in_process + stageTotals.cold_room).toLocaleString('es-CO')}
              </div>
              <div
                className="mt-1.5 text-[11px] font-extrabold uppercase"
                style={{ letterSpacing: '0.08em', color: '#9BA89B' }}
              >
                Tallos · Total
              </div>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={fs.toggle}
              title={fs.isFs ? 'Salir de pantalla completa' : 'Pantalla completa'}
            >
              {fs.isFs ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </Button>
          </div>
        </div>

        {/* Leyenda de estados de proceso */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-5">
          {Object.entries(stageTotals).map(([stage, total]) => (
            <div key={stage} className="flex items-center gap-2">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ background: { unprocessed: '#C3DB5A', in_process: '#6FB07A', cold_room: '#14502A' }[stage] }}
              />
              <span className="text-[14px] font-semibold" style={{ color: '#5C6B60' }}>
                {STAGE_LABEL[stage]}
              </span>
              <span className="text-[14px] font-extrabold" style={{ color: '#143A22' }}>
                {total.toLocaleString('es-CO')}
              </span>
            </div>
          ))}
        </div>

        {groups.length === 0 && (
          <div
            className="text-[14px] font-medium py-8 text-center"
            style={{ color: '#8B9B8F' }}
          >
            No hay inventario registrado todavía.
          </div>
        )}

        <div
          className={cn(
            'grid gap-4',
            fs.isFs
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
              : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
          )}
        >
          {groups.map((g) => (
            <div
              key={g.variety}
              style={{
                background: '#fff',
                border: '1px solid #E6EFE3',
                borderRadius: 20,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '20px 22px',
                  borderBottom: '1px solid #EEF2EC',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div
                    className="font-display"
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 14,
                      background: '#033021',
                      color: '#C3DB5A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: 21,
                    }}
                  >
                    {String(g.variety).charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div
                      className="font-display"
                      style={{
                        fontSize: 25,
                        fontWeight: 800,
                        color: '#143A22',
                        lineHeight: 1.05,
                      }}
                    >
                      {g.variety}
                    </div>
                    <div
                      style={{ fontSize: 14, color: '#7E9285', fontWeight: 600 }}
                    >
                      {g.lengths.length} longitud
                      {g.lengths.length === 1 ? '' : 'es'}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div
                    className="font-display"
                    style={{
                      fontSize: 32,
                      fontWeight: 800,
                      color: '#1E6E3A',
                      lineHeight: 1,
                    }}
                  >
                    {g.total.toLocaleString('es-CO')}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: '0.1em',
                      color: '#9BA89B',
                    }}
                  >
                    TALLOS
                  </div>
                </div>
              </div>
              <div style={{ padding: '20px 22px' }}>
                {g.lengths.map((row, idx) => {
                  const stages = [
                    {
                      key: 'unprocessed',
                      value: row.stages.unprocessed,
                      color: '#C3DB5A',
                      label: 'Sin procesar',
                    },
                    {
                      key: 'in_process',
                      value: row.stages.in_process,
                      color: '#6FB07A',
                      label: 'En proceso',
                    },
                    {
                      key: 'cold_room',
                      value: row.stages.cold_room,
                      color: '#14502A',
                      label: 'Cuarto frío',
                    },
                  ]
                  return (
                    <div
                      key={row.id}
                      style={{
                        marginBottom: idx === g.lengths.length - 1 ? 0 : 20,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: 11,
                        }}
                      >
                        <span
                          className="font-display"
                          style={{ fontSize: 19, fontWeight: 800, color: '#143A22' }}
                        >
                          {row.stemLength}cm
                        </span>
                        <span
                          className="font-display"
                          style={{ fontSize: 19, fontWeight: 800, color: '#1E6E3A' }}
                        >
                          {row.total.toLocaleString('es-CO')}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          height: 11,
                          borderRadius: 999,
                          overflow: 'hidden',
                          marginBottom: 13,
                        }}
                      >
                        {row.total > 0 ? (
                          stages
                            .filter((s) => s.value > 0)
                            .map((s) => (
                              <div
                                key={s.key}
                                style={{
                                  width: `${(s.value / row.total) * 100}%`,
                                  background: s.color,
                                }}
                              />
                            ))
                        ) : (
                          <div style={{ width: '100%', background: '#ECECEA' }} />
                        )}
                      </div>
                      <div
                        style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}
                      >
                        {stages
                          .filter((s) => s.value > 0)
                          .map((s) => (
                            <span
                              key={s.key}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 7,
                              }}
                            >
                              <span
                                style={{
                                  width: 9,
                                  height: 9,
                                  borderRadius: 999,
                                  background: s.color,
                                }}
                              />
                              <span
                                style={{
                                  fontSize: 14,
                                  color: '#5C6B60',
                                  fontWeight: 600,
                                }}
                              >
                                {s.label}
                              </span>
                              <span
                                style={{
                                  fontSize: 14,
                                  fontWeight: 800,
                                  color: '#143A22',
                                }}
                              >
                                {s.value.toLocaleString('es-CO')}
                              </span>
                            </span>
                          ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Selector de periodo del Dashboard (Hoy / Semana / Mes / Personalizado).
 *
 * El cliente pidió poder ver los KPIs en el rango que necesite. El custom
 * abre dos inputs date para from/to. Mantiene el estado en el padre.
 */
function PeriodFilter({ period, setPeriod, range, setRange }) {
  return (
    <div className="flex items-center gap-3">
      <Segmented
        options={PERIOD_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
        value={period}
        onChange={setPeriod}
      />
      {period === 'custom' && (
        <DateMenu>
          <div className="flex flex-col gap-3" style={{ minWidth: 220 }}>
            <label className="text-sm" style={{ color: '#5C6B60', fontWeight: 600 }}>
              Desde
              <input
                type="date"
                value={range.from || ''}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                className="mt-1 w-full rounded-lg px-3 py-2 text-sm"
                style={{ border: '1px solid #E6EFE3', outline: 'none' }}
              />
            </label>
            <label className="text-sm" style={{ color: '#5C6B60', fontWeight: 600 }}>
              Hasta
              <input
                type="date"
                value={range.to || ''}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                className="mt-1 w-full rounded-lg px-3 py-2 text-sm"
                style={{ border: '1px solid #E6EFE3', outline: 'none' }}
              />
            </label>
          </div>
        </DateMenu>
      )}
    </div>
  )
}

const WEEK_DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

/** "Tallos despachados" — barras por día de la semana actual. */
function DispatchBarChart({ sales }) {
  const { days, total } = useMemo(() => {
    const today = startOfDay(new Date())
    const dow = today.getDay()
    const monday = addDays(today, dow === 0 ? -6 : 1 - dow)
    const byKey = new Map()
    for (const s of sales || []) {
      const key = String(s.deliveryDate || s.saleDate || s.date || '').slice(0, 10)
      if (!key) continue
      const stems =
        Number(s.totalStems) || flattenSaleLines(s).reduce((a, l) => a + l.quantity, 0)
      byKey.set(key, (byKey.get(key) || 0) + stems)
    }
    const out = []
    let tot = 0
    for (let i = 0; i < 7; i++) {
      const d = addDays(monday, i)
      const value = byKey.get(ymd(d)) || 0
      tot += value
      out.push({ label: WEEK_DAY_LABELS[i], value, isToday: sameDay(d, today) })
    }
    return { days: out, total: tot }
  }, [sales])

  const max = Math.max(1, ...days.map((d) => d.value))

  return (
    <div style={{ background: '#fff', border: '1px solid #E6EFE3', borderRadius: 20, padding: '24px 26px' }}>
      <div className="font-display" style={{ fontSize: 22, fontWeight: 800, color: '#143A22' }}>
        Tallos despachados
      </div>
      <div style={{ fontSize: 14, color: '#8B9B8F', fontWeight: 500, margin: '4px 0 20px' }}>
        Esta semana · {total.toLocaleString('es-CO')} tallos
      </div>
      <div className="flex items-end" style={{ gap: 10, height: 200 }}>
        {days.map((d, i) => {
          const h = d.value > 0 ? Math.max(8, Math.round((d.value / max) * 150)) : 0
          return (
            <div key={i} className="flex flex-col items-center" style={{ flex: 1, justifyContent: 'flex-end', height: '100%' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#143A22', marginBottom: 6, opacity: d.value > 0 ? 1 : 0 }}>
                {d.value.toLocaleString('es-CO')}
              </div>
              {d.value > 0 ? (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 46,
                    height: h,
                    borderRadius: 10,
                    background: d.isToday ? '#14502A' : '#A9C93C',
                  }}
                />
              ) : (
                <div style={{ width: 24, height: 4, borderRadius: 999, background: '#E6EFE3' }} />
              )}
              <div style={{ fontSize: 13, fontWeight: 700, color: d.isToday ? '#143A22' : '#9BA89B', marginTop: 10 }}>
                {d.label}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** "Inventario por estado" — donut por etapa. */
function InventoryDonut({ inventory }) {
  const t = useMemo(() => {
    let u = 0
    let p = 0
    let c = 0
    for (const r of inventory || []) {
      u += Number(r.stages?.unprocessed?.total) || 0
      p += Number(r.stages?.in_process?.total) || 0
      c += Number(r.stages?.cold_room?.total) || 0
    }
    return { u, p, c, total: u + p + c }
  }, [inventory])

  const segs = [
    { label: 'Sin procesar', value: t.u, color: '#A9C93C' },
    { label: 'En proceso', value: t.p, color: '#6FB07A' },
    { label: 'Cuarto frío', value: t.c, color: '#14502A' },
  ].filter((s) => s.value > 0)
  const sum = t.total || 1
  let acc = 0
  const stops = segs
    .map((s) => {
      const a = (acc / sum) * 100
      acc += s.value
      return `${s.color} ${a}% ${(acc / sum) * 100}%`
    })
    .join(', ')
  const bg = t.total > 0 ? `conic-gradient(${stops})` : '#ECECEA'

  return (
    <div style={{ background: '#fff', border: '1px solid #E6EFE3', borderRadius: 20, padding: '24px 26px' }}>
      <div className="font-display" style={{ fontSize: 22, fontWeight: 800, color: '#143A22', marginBottom: 18 }}>
        Inventario por estado
      </div>
      <div className="flex items-center" style={{ gap: 28 }}>
        <div style={{ position: 'relative', width: 132, height: 132, flex: 'none', borderRadius: 999, background: bg }}>
          <div
            style={{
              position: 'absolute',
              inset: 20,
              background: '#fff',
              borderRadius: 999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span className="font-display" style={{ fontSize: 24, fontWeight: 800, color: '#143A22', lineHeight: 1 }}>
              {t.total.toLocaleString('es-CO')}
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#8B9B8F' }}>tallos</span>
          </div>
        </div>
        <div className="flex flex-col" style={{ gap: 12, flex: 1, minWidth: 0 }}>
          {[
            { label: 'Sin procesar', value: t.u, color: '#A9C93C' },
            { label: 'En proceso', value: t.p, color: '#6FB07A' },
            { label: 'Cuarto frío', value: t.c, color: '#14502A' },
          ].map((s) => (
            <div key={s.label} className="flex items-center" style={{ gap: 10 }}>
              <span style={{ width: 12, height: 12, borderRadius: 4, background: s.color, flex: 'none' }} />
              <span style={{ fontSize: 15, fontWeight: 600, color: '#5C6B60', flex: 1 }}>{s.label}</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#143A22' }}>{s.value.toLocaleString('es-CO')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * "Próximos despachos 2" — versión compacta (tarjetas pequeñas) para el
 * resumen. "Ver agenda" abre el modal con el componente completo.
 */
function CompactDispatches({ sales, clientsById, onOpenSale, onVerAgenda }) {
  const upcoming = useMemo(() => {
    const today = startOfDay(new Date())
    return (sales || [])
      .filter((s) => {
        const k = s.deliveryDate || s.saleDate || s.date
        if (!k) return false
        return startOfDay(new Date(k)) >= today
      })
      .sort((a, b) =>
        String(a.deliveryDate || a.saleDate || a.date).localeCompare(
          String(b.deliveryDate || b.saleDate || b.date),
        ),
      )
      .slice(0, 6)
  }, [sales])

  return (
    <div style={{ background: '#fff', border: '1px solid #E6EFE3', borderRadius: 20, padding: '24px 26px' }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 18, gap: 12 }}>
        <div className="font-display" style={{ fontSize: 22, fontWeight: 800, color: '#143A22' }}>
          Próximos despachos
        </div>
        <button
          type="button"
          onClick={onVerAgenda}
          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#1E6E3A', fontSize: 15, fontWeight: 700 }}
        >
          Ver agenda →
        </button>
      </div>
      {upcoming.length === 0 ? (
        <div style={{ fontSize: 14, color: '#8B9B8F', padding: '12px 0' }}>Sin despachos próximos.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {upcoming.map((s) => {
            const labels = resolveClientLabels(s, clientsById)
            const eff = effectiveStatus(s)
            const badge = SALE_STATUS_BADGE[eff] || { bg: '#E3F0D5', fg: '#1E6E3A' }
            const d = new Date(s.deliveryDate || s.saleDate || s.date)
            const stems =
              Number(s.totalStems) || flattenSaleLines(s).reduce((a, l) => a + l.quantity, 0)
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onOpenSale?.(s)}
                className="text-left press"
                style={{ background: '#fff', border: '1px solid #EAEFE7', borderRadius: 14, padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}
              >
                <div
                  className="flex flex-col items-center justify-center flex-none"
                  style={{ width: 48, height: 48, borderRadius: 12, background: '#033021', color: '#C3DB5A' }}
                >
                  <span style={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>{d.getDate()}</span>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em' }}>
                    {MONTH_NAMES_SHORT[d.getMonth()].toUpperCase()}
                  </span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="truncate" style={{ fontSize: 15, fontWeight: 700, color: '#143A22' }}>
                    {labels.final}
                  </div>
                  <div style={{ fontSize: 13, color: '#8B9B8F', fontWeight: 500, marginTop: 2 }}>
                    {stems.toLocaleString('es-CO')} tallos{s.dispatchTime ? ` · ${s.dispatchTime}` : ''}
                  </div>
                </div>
                <span
                  className="flex-none"
                  style={{ background: badge.bg, color: badge.fg, fontSize: 12, fontWeight: 700, padding: '5px 10px', borderRadius: 999, whiteSpace: 'nowrap' }}
                >
                  {SALE_STATUS_LABELS[eff] || eff}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

const DASHBOARD_VIEWS = [
  { value: 'resumen', label: 'Resumen' },
  { value: 'despachos', label: 'Despachos' },
  { value: 'inventario', label: 'Inventario' },
]

export default function DashboardPage() {
  const [period, setPeriod] = useState('today')
  const [range, setRange] = useState({ from: '', to: '' })
  // Vista del dashboard: resumen (nuevo) | despachos (agenda completa) |
  // inventario (detalle por variedad/longitud).
  const [view, setView] = useState('resumen')
  // Modal "Ver agenda" — abre el componente completo de próximos despachos.
  const [agendaOpen, setAgendaOpen] = useState(false)

  // Construir el endpoint con query string solo cuando el rango personalizado
  // tiene ambas fechas; de lo contrario llamamos sin from/to.
  const statsEndpoint = useMemo(() => {
    const qs = new URLSearchParams({ period })
    if (period === 'custom') {
      if (!range.from || !range.to) return null
      qs.set('from', range.from)
      qs.set('to', range.to)
    }
    return `/stats?${qs.toString()}`
  }, [period, range.from, range.to])

  const { data, loading, error, refresh: refreshStats } = useApi(
    statsEndpoint,
    { enabled: Boolean(statsEndpoint) },
  )
  const salesApi = useApi('/sales')
  const inventoryApi = useApi('/inventory')
  const clientsApi = useApi('/clients')
  const [openSale, setOpenSale] = useState(null)

  // Mapa id→cliente para resolver la jerarquía mayorista↔final en tarjetas y
  // en el modal de detalle sin rebuscar en cada render.
  const clientsById = useMemo(() => {
    const m = new Map()
    for (const c of clientsApi.data || []) m.set(c.id, c)
    return m
  }, [clientsApi.data])

  // Despachos de HOY (conteo + tallos) para el KPI del resumen.
  const dispatchToday = useMemo(() => {
    const today = startOfDay(new Date())
    let count = 0
    let stems = 0
    for (const s of salesApi.data || []) {
      const k = s.deliveryDate || s.saleDate || s.date
      if (!k) continue
      if (sameDay(startOfDay(new Date(k)), today)) {
        count += 1
        stems += Number(s.totalStems) || flattenSaleLines(s).reduce((a, l) => a + l.quantity, 0)
      }
    }
    return { count, stems }
  }, [salesApi.data])

  /** Disparador único de refresco tras una transición manual de status. */
  const refreshAll = () => {
    refreshStats()
    salesApi.refresh()
    inventoryApi.refresh()
  }

  return (
    <>
      <ModuleShell
        title="Dashboard"
        subtitle="Resumen operativo de ventas, compras e inventario"
        icon={Calendar}
        filters={
          <FilterBar
            left={<Segmented options={DASHBOARD_VIEWS} value={view} onChange={setView} />}
            right={
              <PeriodFilter
                period={period}
                setPeriod={setPeriod}
                range={range}
                setRange={setRange}
              />
            }
          />
        }
      >

      {loading ? (
        <p className="font-medium" style={{ color: '#8B9B8F' }}>Cargando…</p>
      ) : error ? (
        <p className="font-medium" style={{ color: '#C0392B' }}>Error: {String(error.message)}</p>
      ) : data ? (
        <>
          {view === 'resumen' && (
            <div className="space-y-6">
              {/* KPIs (sin valores en USD ni conteo de clientes, por feedback). */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi
                  icon={Scissors}
                  label="Tallos vendidos"
                  value={Number(data.totals?.stemsSold || 0).toLocaleString('es-CO')}
                  pill={`${Number(data.counts?.salesInRange || 0).toLocaleString('es-CO')} venta${data.counts?.salesInRange === 1 ? '' : 's'}`}
                  hero
                />
                <Kpi
                  icon={ShoppingCart}
                  label="Tallos comprados"
                  value={Number(data.totals?.stemsBought || 0).toLocaleString('es-CO')}
                  pill={`${Number(data.counts?.purchasesInRange || 0).toLocaleString('es-CO')} compra${data.counts?.purchasesInRange === 1 ? '' : 's'}`}
                />
                <Kpi
                  icon={Package}
                  label="Ventas abiertas"
                  value={data.counts?.openSales ?? 0}
                  pill={`de ${Number(data.counts?.sales || 0).toLocaleString('es-CO')} en total`}
                />
                <Kpi
                  icon={Calendar}
                  label="Despachos hoy"
                  value={dispatchToday.count}
                  pill={`${dispatchToday.stems.toLocaleString('es-CO')} tallos`}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <DispatchBarChart sales={salesApi.data || []} />
                <InventoryDonut inventory={inventoryApi.data || []} />
              </div>

              <CompactDispatches
                sales={salesApi.data || []}
                clientsById={clientsById}
                onOpenSale={setOpenSale}
                onVerAgenda={() => setAgendaOpen(true)}
              />
            </div>
          )}

          {view === 'despachos' && (
            <UpcomingDispatches
              sales={salesApi.data || []}
              onOpenSale={setOpenSale}
              clientsById={clientsById}
              onStatusChanged={refreshAll}
              period={period}
              range={range}
            />
          )}

          {view === 'inventario' && (
            <InventoryDetail inventory={inventoryApi.data || []} />
          )}
        </>
      ) : null}
      </ModuleShell>

      <SaleDetailModal
        sale={openSale}
        onClose={() => setOpenSale(null)}
        clientsById={clientsById}
        onStatusChanged={() => {
          refreshAll()
          setOpenSale(null)
        }}
      />

      {/* Modal "Ver agenda": componente completo de próximos despachos. */}
      <Modal
        open={agendaOpen}
        onClose={() => setAgendaOpen(false)}
        title="Agenda de despachos"
        subtitle="Pedidos programados por día"
        size="2xl"
      >
        <UpcomingDispatches
          sales={salesApi.data || []}
          onOpenSale={(s) => {
            setAgendaOpen(false)
            setOpenSale(s)
          }}
          clientsById={clientsById}
          onStatusChanged={refreshAll}
          period={period}
          range={range}
        />
      </Modal>
    </>
  )
}
